import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { VwAuthService, VwAppStateService } from 'vwappservices';
import { DataManipService } from '../data-sharing.service';
import { CommonServicesService } from '../common-services.service';
import { CurrentratesService } from 'src/app/default-layout/market-ticker/currentrates.service';

// restoreSession() only re-obtains the access token — it doesn't know about loginData
// (username/branch/clientkey, used by the header), currentRates (the market-rate ticker), or
// the System Parameters date/number display format, all of which are only ever set in-memory
// at login. A hard refresh on any deep route keeps the token but wipes all three, so
// rehydrate them here before letting the route through.
async function rehydratePostRefreshState(dataManipSrvc: DataManipService, commonSrvc: CommonServicesService, currentRatesService: CurrentratesService): Promise<void> {
  dataManipSrvc.restoreLoginDataIfMissing();

  // currentRates is live market pricing, not just display info — restore it with a fresh
  // API call rather than a cached copy, since a stale gold/silver rate is actively misleading
  // for a jewelry-purchase app, not just cosmetically wrong.
  if ((!currentRatesService.currentRates || currentRatesService.currentRates.length === 0) && dataManipSrvc.loginData?.branchid) {
    currentRatesService.currentRates = await commonSrvc.getCurrentRates(dataManipSrvc.loginData.branchid);
  }

  // loadFormatSettings() is a no-op once already loaded this session, so calling it
  // unconditionally on every guard check (like restoreLoginDataIfMissing above) is cheap —
  // without this, any date/number format saved on System Parameters reverted to the
  // hardcoded default on the next hard refresh or fresh tab, regardless of what was saved.
  await dataManipSrvc.loadFormatSettings();
}

export const authGuard: CanActivateFn = async (_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
  const appState = inject(VwAppStateService);
  const vwAuth = inject(VwAuthService);
  const router = inject(Router);
  const dataManipSrvc = inject(DataManipService);
  const commonSrvc = inject(CommonServicesService);
  const currentRatesService = inject(CurrentratesService);

  if (appState.token) {
    // Token survived (e.g. a normal client-side route change) — this state should already be
    // in memory too, but rehydrate defensively in case this is the first guard check after
    // a hard refresh restored the token via some other path.
    await rehydratePostRefreshState(dataManipSrvc, commonSrvc, currentRatesService);
    return true;
  }

  const restored = await vwAuth.restoreSession();
  if (restored) {
    await rehydratePostRefreshState(dataManipSrvc, commonSrvc, currentRatesService);
    return true;
  }

  router.navigate(['/login']);
  return false;
};
